import { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { apiErrorMessage } from '@/api/apiError';
import { useListOrgTokensQuery } from '@/api/hubApi';
import type { OrgTokenListItem, OrgTokenStatus } from '@/api/orgTokenTypes';
import { CreateOrgTokenDialog } from '@/pages/CreateOrgTokenDialog';

const STATUS_COLOR: Record<OrgTokenStatus, 'success' | 'default' | 'warning'> = {
    active: 'success',
    revoked: 'default',
    expired: 'warning',
};

function formatDate(value: string | null): string {
    if (!value) return '—';
    return new Date(value).toLocaleString();
}

export function OrganizationsListPage() {
    const { data, isLoading, isFetching, error, refetch } = useListOrgTokensQuery();
    const [createOpen, setCreateOpen] = useState(false);

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h5">Organizations</Typography>
                    {isFetching && <CircularProgress size={20} />}
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateOpen(true)}
                >
                    Create organization
                </Button>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => refetch()}>
                            Retry
                        </Button>
                    }
                >
                    {apiErrorMessage(error, 'Failed to load organizations')}
                </Alert>
            )}

            {isLoading ? (
                <CircularProgress />
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Label</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Expires</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data ?? []).map((row: OrgTokenListItem) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{row.org.name}</TableCell>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>
                                        {row.org_type}
                                        {row.org.kind ? ` · ${row.org.kind}` : ''}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={row.status}
                                            color={STATUS_COLOR[row.status]}
                                        />
                                    </TableCell>
                                    <TableCell>{row.label || '—'}</TableCell>
                                    <TableCell>{formatDate(row.created_at)}</TableCell>
                                    <TableCell>{formatDate(row.expires_at)}</TableCell>
                                </TableRow>
                            ))}
                            {data && data.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No organizations yet — create one to get started.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <CreateOrgTokenDialog open={createOpen} onClose={() => setCreateOpen(false)} />
        </Box>
    );
}
